/* tslint:disable:jsx-no-lambda WI:47606 */
import CalendarAllDayWell from './CalendarAllDayWell';
import CalendarExpandableAllDayWell from './CalendarExpandableAllDayWell';
import CalendarGrid from './CalendarGrid';
import FocusableSurfaceComponent from './FocusableSurfaceComponent';
import {
    default as CalendarDayConfig,
    CalendarDayContentConfig,
} from '../interfaces/CalendarDayConfig';
import CalendarDayContent from '../interfaces/CalendarDayContent';
import SurfaceStore from '../interfaces/SurfaceStore';
import SurfaceConstants from '../styles/SurfaceConstants';
import { DEFAULT_PANEL_MARGIN_RIGHT } from '../utils/constants';
import InDayScrollContainer from './InDayScrollContainer';
import toPixelString from '../utils/toPixelString';
import { observer } from 'mobx-react-lite';
import { getRTL } from '@fluentui/react/lib/Utilities';
import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';
import * as React from 'react';
import { useCustomTimeout } from 'owa-react-hooks/lib/useCustomTimeout';
import { useId } from '@uifabric/react-hooks';
import {
    addNewGridViewRef,
    addNewAllDayPanelViewRef,
    resetCalendarDayListBounds,
} from '../actions/calendarDayListBoundsMutators';
import CalendarDefaultSurfaceComponent from './CalendarDefaultSurfaceComponent';
import { StoreContextProvider } from './StoreContextProvider';

let styles = require('./CalendarDayList.scss');
let classNames = require('classnames/bind').bind(styles);

const MIN_BOUNDS_PADDING = 16;

/**
 * Props for calendar day list control
 */
export interface CalendarDayListProps {
    config: CalendarDayConfig;
    store: SurfaceStore;
    shouldFillContainer?: boolean;
}

export interface CalendarDayListHandle {
    getPopupBounds(anchorElement: HTMLElement): ClientRect;
}

/**
 * CalendarDayList shows calendar day grids with the ability to show calendar views side by side for each selected calendar.
 */
export default observer(
    function CalendarDayList(props: CalendarDayListProps, ref: React.Ref<CalendarDayListHandle>) {
        const isRTL = getRTL();

        const gridHeadersScrollRef = React.useRef<HTMLElement>();
        const allDayScrollRef = React.useRef<HTMLElement>();
        const allDayPanelScroll = e => {
            const container: HTMLDivElement = e.currentTarget;
            // move the inDayPanel along with the allDayPanel to keep them aligned
            props.store.calendarDayListBounds.inDayScrollRef &&
                (props.store.calendarDayListBounds.inDayScrollRef.element.scrollLeft =
                    container.scrollLeft);
        };

        React.useImperativeHandle(
            ref,
            () => ({
                getPopupBounds(anchorElement: HTMLElement) {
                    if (
                        props.store.calendarDayListBounds.gridViewRefs &&
                        props.store.calendarDayListBounds.gridViewRefs.length > 0 &&
                        props.store.calendarDayListBounds.inDayScrollRef
                    ) {
                        // Check to see if the anchorElement is part of any gridViewRefs
                        let currentRef = props.store.calendarDayListBounds.gridViewRefs.filter(
                            a => a && a.element.contains(anchorElement)
                        );
                        if (currentRef.length) {
                            const scrollBounds =
                                props.store.calendarDayListBounds.inDayScrollRef.bounds;
                            const allDayRefs =
                                props.store.calendarDayListBounds.allDayPanelViewRefs;
                            const allDayWellHeight =
                                allDayRefs[allDayRefs.length - 1].bounds.bottom -
                                allDayRefs[allDayRefs.length - 1].bounds.top;
                            return {
                                top: scrollBounds.top - allDayWellHeight + MIN_BOUNDS_PADDING,
                                bottom: scrollBounds.bottom - MIN_BOUNDS_PADDING,
                                height:
                                    scrollBounds.height + allDayWellHeight - MIN_BOUNDS_PADDING * 2,
                                left: scrollBounds.left + MIN_BOUNDS_PADDING,
                                right: scrollBounds.right - MIN_BOUNDS_PADDING,
                                width: scrollBounds.width - MIN_BOUNDS_PADDING * 2,
                            };
                        } else {
                            // if we did not find anchorElement to be part of any gridViewRefs, then check if it is part of allDayPanelViewRefs
                            if (
                                props.store.calendarDayListBounds.allDayPanelViewRefs &&
                                props.store.calendarDayListBounds.allDayPanelViewRefs.length > 0
                            ) {
                                currentRef = props.store.calendarDayListBounds.allDayPanelViewRefs.filter(
                                    a => a && a.element.contains(anchorElement)
                                );
                                if (currentRef.length) {
                                    const bounds = currentRef[currentRef.length - 1].bounds;
                                    const scrollBounds =
                                        props.store.calendarDayListBounds.inDayScrollRef.bounds;
                                    const scrollLeft =
                                        props.store.calendarDayListBounds.inDayScrollRef.element
                                            .scrollLeft;
                                    if (bounds) {
                                        return {
                                            top: bounds.top,
                                            bottom: Number.POSITIVE_INFINITY,
                                            height: Number.POSITIVE_INFINITY,
                                            left:
                                                scrollBounds.left - scrollLeft + MIN_BOUNDS_PADDING,
                                            right:
                                                scrollBounds.right -
                                                scrollLeft -
                                                MIN_BOUNDS_PADDING,
                                            width: scrollBounds.width - MIN_BOUNDS_PADDING * 2,
                                        };
                                    }
                                }
                            }
                        }
                    }
                    // if anchor is not in the component, we have no constraints
                    return null;
                },
            }),
            []
        );
        const allDayPanelViewRefsFunction = (element: HTMLElement, index: number) => {
            if (element) {
                setTimeout(() => {
                    // build a list of grid refs per calendar, so that we can anchor peeks
                    // based on which calendar grid the clicked item belongs to.
                    addNewAllDayPanelViewRef(element, props.store, index);
                }, 0);
            }
        };
        const gridHeadersScrollRefFunction = React.useCallback((element: HTMLElement) => {
            gridHeadersScrollRef.current = element;
        }, []);
        const allDayScrollRefFunction = React.useCallback((element: HTMLElement) => {
            allDayScrollRef.current = element;
        }, []);
        // clear the existing refs, so we can rebuild new list

        React.useEffect(() => {
            resetCalendarDayListBounds(props.store);
            return () => resetCalendarDayListBounds(props.store);
        }, []);

        const { config } = props;
        const minWidthStyle = React.useCallback(
            (ignoreSideMargin: boolean) => {
                const panelMarginRightWidth = config.panelMarginRight || DEFAULT_PANEL_MARGIN_RIGHT;
                const panelMarginRightWidthString = toPixelString(panelMarginRightWidth);
                const marginSide = isRTL ? 'marginLeft' : 'marginRight';
                const style = { [marginSide]: ignoreSideMargin ? 0 : panelMarginRightWidthString };
                if (config.minWidthProvider?.()) {
                    style.minWidth = toPixelString(config.minWidthProvider());
                }
                return style;
            },
            [config.panelMarginRight, config.minWidthProvider]
        );

        if (!config.calendarDayContentConfigProvider) {
            return null;
        }
        let calendarContentConfigs = config.calendarDayContentConfigProvider();
        if (!calendarContentConfigs || calendarContentConfigs.length <= 0) {
            return null;
        }

        // track the weather icon to be shown only once and not for all calendars
        let showedHeaderAddOnOnce: boolean = false;
        const allDayWellExpandable = config.supportExpandableAllDayWell
            ? config.supportExpandableAllDayWell()
            : false;

        return (
            <FocusableSurfaceComponent
                tabOrderProvider={() =>
                    mergeTabs(calendarContentConfigs.map(config => config.content))
                }
                setFocusedElement={config.setFocusedElement}
                ref={config.surfaceComponent}>
                <StoreContextProvider value={props.store}>
                    <div className={styles.page}>
                        <div className={styles.topPanel}>
                            <div
                                key={'topPanel'}
                                style={{
                                    display: 'flex',
                                    flexShrink: 0,
                                }}>
                                {calendarContentConfigs[0].content.timeStripHeaderContent()}
                            </div>
                            <div
                                className={classNames(
                                    styles.scrollableContainer,
                                    props.shouldFillContainer
                                        ? styles.filledScrollableContainer
                                        : null
                                )}
                                onFocus={allDayPanelScroll} // focus events cause panel to scroll but do not fire scroll event
                                onScroll={allDayPanelScroll}
                                ref={allDayScrollRefFunction}>
                                {calendarContentConfigs.map((contentConfig, i, array) => {
                                    let hideHeaderAddOn = showedHeaderAddOnOnce;
                                    showedHeaderAddOnOnce = true;
                                    return (
                                        <div
                                            key={i}
                                            className={styles.topContainer}
                                            style={minWidthStyle(i === array.length - 1)} // ignore side margin for last item
                                            ref={gridElement =>
                                                allDayPanelViewRefsFunction(gridElement, i)
                                            }>
                                            {!config.hideTopHeaders &&
                                                calendarContentConfigs.length > 1 &&
                                                contentConfig.contentDescription && (
                                                    <div className={styles.contentHeader}>
                                                        {contentConfig.contentDescription()}
                                                    </div>
                                                )}
                                            <AllDayHeaderContent
                                                store={props.store}
                                                contentConfig={contentConfig}
                                                config={config}
                                                configCount={calendarContentConfigs.length}
                                                hideHeaderAddOn={hideHeaderAddOn}
                                            />
                                            <AllDayContent
                                                allDayWellExpandable={allDayWellExpandable}
                                                store={props.store}
                                                contentConfig={contentConfig}
                                                configCount={calendarContentConfigs.length}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className={classNames(styles.inDayPanel)}>
                            <div
                                aria-hidden="true" // hidden row headers because events themselves say what time they are, so don't need to read table headings
                                className={styles.gridHeadersScrollContainer}
                                ref={gridHeadersScrollRefFunction}>
                                <div key={'inDayPanel'} className={styles.timeContainer}>
                                    {calendarContentConfigs[0].content.timeStripContent()}
                                </div>
                            </div>
                            <InDayContent
                                store={props.store}
                                gridHeadersScrollRef={gridHeadersScrollRef}
                                allDayScrollRef={allDayScrollRef}
                                calendarContentConfigs={calendarContentConfigs}
                                minWidthStyle={minWidthStyle}
                                config={config}
                            />
                        </div>
                    </div>
                </StoreContextProvider>
            </FocusableSurfaceComponent>
        );
    },
    { forwardRef: true }
);

const AllDayHeaderContent = observer(function AllDayHeaderContent({
    store,
    config,
    contentConfig,
    hideHeaderAddOn,
    configCount,
}: {
    store: SurfaceStore;
    config: CalendarDayConfig;
    contentConfig: CalendarDayContentConfig;
    hideHeaderAddOn: boolean;
    configCount: number;
}) {
    const headerContent = contentConfig.content?.headers(hideHeaderAddOn);

    if (config.hideTopHeaders || !headerContent) {
        return null;
    }

    const { component, additionalProps } = contentConfig.allDayHeaderComponent || {};
    const AllDayHeaderComponentType = component || CalendarDefaultSurfaceComponent;

    return (
        <AllDayHeaderComponentType
            className={classNames(styles.header, {
                stackedHeader: configCount > 1,
            })}
            handlers={undefined}
            additionalProps={additionalProps}>
            {headerContent}
        </AllDayHeaderComponentType>
    );
});

const AllDayContent = observer(function AllDayContent({
    store,
    contentConfig,
    configCount,
    allDayWellExpandable,
}: {
    store: SurfaceStore;
    contentConfig: CalendarDayContentConfig;
    configCount: number;
    allDayWellExpandable: boolean;
}) {
    const content = contentConfig.content.allDayContent();
    const { component: AllDayComponentType, additionalProps } = contentConfig.allDayComponent || {};
    const {
        visibleRowCount,
        allDayUncollapsedRowCount,
        allDayRowCount,
    } = contentConfig.content.allDayContentProperties();

    if (AllDayComponentType) {
        return (
            <AllDayComponentType
                className={undefined}
                handlers={undefined}
                additionalProps={additionalProps}>
                {content}
            </AllDayComponentType>
        );
    } else {
        return allDayWellExpandable ? (
            <CalendarExpandableAllDayWell
                allDayContent={content}
                isSplitView={configCount > 1}
                visibleRowCount={visibleRowCount}
                allDayUncollapsedRowCount={allDayUncollapsedRowCount}
            />
        ) : (
            <CalendarAllDayWell allDayContent={content} allDayRowCount={allDayRowCount} />
        );
    }
});

const InDayContent = observer(function InDayContent({
    store,
    gridHeadersScrollRef,
    allDayScrollRef,
    calendarContentConfigs,
    minWidthStyle,
    config,
}: {
    store: SurfaceStore;
    gridHeadersScrollRef: React.RefObject<HTMLElement>;
    allDayScrollRef: React.RefObject<HTMLElement>;
    calendarContentConfigs: CalendarDayContentConfig[];
    minWidthStyle: (ignoreSideMargin: boolean) => React.CSSProperties;
    config: CalendarDayConfig;
}) {
    const [setInDayPanelScrollTimer, , isInDayPanelScrollTimerActive] = useCustomTimeout();
    const addNewGridViewTimerIds = React.useRef<{ [elementId: string]: number }>({});

    const inDayPanelScroll = React.useCallback(evt => {
        if (config.onSurfaceScroll) {
            config.onSurfaceScroll();
        }
        const container: HTMLDivElement = evt.currentTarget;

        if (!isInDayPanelScrollTimerActive()) {
            // wrap in setTimeout to prevent layout recalulation during rendering
            setInDayPanelScrollTimer(() => {
                if (gridHeadersScrollRef.current) {
                    gridHeadersScrollRef.current.scrollTop = container.scrollTop;
                }
                if (allDayScrollRef.current) {
                    allDayScrollRef.current.scrollLeft = container.scrollLeft;
                }
            }, 0);
        }
    }, []);

    const gridViewRefsFunction = React.useCallback((element: HTMLElement, index: number) => {
        if (element && !addNewGridViewTimerIds.current[element.id]) {
            addNewGridViewTimerIds.current[element.id] = window.setTimeout(() => {
                // build a list of grid refs per calendar, so that we can anchor peeks
                // based on which calendar grid the clicked item belongs to.
                addNewGridViewRef(element, store, index);
                delete addNewGridViewTimerIds.current[element.id];
            }, 0);
        }
    }, []);

    React.useEffect(() => {
        return () => {
            Object.values(addNewGridViewTimerIds.current).forEach(id => {
                delete addNewGridViewTimerIds.current[id];
                window.clearTimeout(id);
            });
        };
    }, []);

    const itemsPanelStyle: React.CSSProperties = {
        height: toPixelString(
            SurfaceConstants.HourRowHeight *
                (TimeConstants.MillisecondsInOneDay / TimeConstants.MillisecondsIn1Hour)
        ),
    };

    const inDayContentChildren = (
        <div className={styles.growFlexBox} style={itemsPanelStyle}>
            {calendarContentConfigs.map((config, idx, array) => (
                <InDayContentChild
                    key={idx}
                    calendarContentConfig={config}
                    minWidthStyle={minWidthStyle(idx === array.length - 1)} // ignore side margin for last item
                    gridViewRefsFunction={gridViewRefsFunction}
                    index={idx}
                />
            ))}
        </div>
    );

    const { component, additionalProps } = calendarContentConfigs[0]?.inDayComponent || {};
    const InDayComponentType = component || InDayScrollContainer;

    return (
        <InDayComponentType
            className={classNames(styles.scrollContainer, 'customScrollBar')}
            handlers={{
                onScroll: inDayPanelScroll,
            }}
            additionalProps={additionalProps}>
            {inDayContentChildren}
        </InDayComponentType>
    );
});

const InDayContentChild = observer(function InDayContentChild({
    calendarContentConfig,
    minWidthStyle,
    gridViewRefsFunction,
    index,
}: {
    calendarContentConfig: CalendarDayContentConfig;
    minWidthStyle: React.CSSProperties;
    gridViewRefsFunction: (element: HTMLElement, index: number) => void;
    index: number;
}) {
    const elementId = useId();
    return (
        <div
            id={elementId}
            className={styles.renderPanel}
            style={minWidthStyle}
            ref={
                gridElement => gridViewRefsFunction(gridElement, index) // passing an arrow function here ensures that a new function instance is created everytime, and as a result the ref function gets called even when same element updates( which is case when component udates due to change in calendar selection)
            }>
            <CalendarGrid contentConfig={calendarContentConfig} />
        </div>
    );
});

function mergeTabs(contents: CalendarDayContent[]): string[] {
    let results = [];
    // When merging tabs we have to remove surface headers from all except first content stream
    // and add the contentHeaders
    contents.forEach((content, index) => {
        results.push('contentHeader_' + index);
        results.push(
            ...content
                .tabOrderProvider()
                .filter(tabId => index == 0 || tabId.search('surfaceHeader_') < 0)
        );
    });
    return results;
}
